/**
 * Single source of truth for employer job post / edit / draft forms.
 * All surfaces must use jobDocumentToFormValues + formValuesToJobPayload.
 */
import { getRequirementPackById, packCredentialingItems } from '../requirementPacks';
import { getJobCertRequirements } from '../certificationMatching';
import { getCanonicalJobDescription } from '../jobDisplay';
import { normalizeJobList, normalizeJobText } from './jobPostingSections';
import { JOB_SUMMARY_MAX_CHARS } from './descriptionLength';
import {
  cleanCustomQuestionsForSave,
  customQuestionDefinitionErrors,
  normalizeCustomQuestions,
  normalizeStandardScreeningKeys,
  type CustomQuestionType,
  type StandardScreeningKey,
} from '../applications/applicationForm';
import type {
  JobCredentialingRequirements,
  OnboardingSpeedPreference,
  EMTShiftTypePreference,
  EMTEmploymentTypePreference,
} from '../../types/firestore';

export type JobTypeOption =
  | 'full_time'
  | 'part_time'
  | 'per_diem'
  | 'contract'
  | 'temporary'
  | 'emergency_deployment';
export type LocationTypeOption = 'in_person' | 'hybrid' | 'remote';
export type PayVisibilityOption = 'range' | 'starting_at' | 'up_to' | 'exact';
export type PayUnitOption = 'hour' | 'year' | 'shift';
export type HiringTimelineOption =
  | 'immediately'
  | '1_2_weeks'
  | '2_4_weeks'
  | '1_3_months'
  | 'not_sure';
/**
 * '24_hour' added for Item 2 (EMT shift preferences) — no job-posting schedule
 * option previously existed for 24-hour shifts, so a "24-hour" EMT preference
 * had nothing real to match against.
 */
export type ScheduleOption = 'day' | 'night' | 'rotating' | 'weekends' | '24_hour' | 'flexible' | '';
export type HoursModeOption = 'fixed' | 'variable' | '';

/**
 * The employer's own questions (applicationForm.ts holds the types, limits and checks):
 * 'text' short answer, 'longtext', 'yesno', 'choice' (with options), 'date', and legacy 'likert'.
 */
export type ScreeningQuestionType = CustomQuestionType;
export type ScreeningQuestion = {
  id: string;
  type: ScreeningQuestionType;
  prompt: string;
  required: boolean;
  /** Multiple choice only. */
  options?: string[];
};

/**
 * Employer-configured video introduction request, set at job-posting time (not per-application
 * afterward — see JobDetail.tsx's apply flow and EmployerApplicationDetail.tsx's supplementary
 * after-the-fact "Request video introduction" action for the older per-application path this
 * replaces as the primary flow). Stored on the job doc as `videoIntroRequest`; presence with
 * `enabled: true` means applicants see the custom prompt and an optional response uploader.
 */
export type VideoIntroRequest = {
  enabled: boolean;
  prompt: string;
};

/** EMT shift-preference UI options (Item 2). Values match ScheduleOption tokens. */
export const SHIFT_TYPE_PREFERENCE_OPTIONS: Array<[EMTShiftTypePreference, string]> = [
  ['day', 'Day'],
  ['night', 'Night'],
  ['rotating', 'Rotating'],
  ['24_hour', '24-Hour'],
];

/** Lookup form of SHIFT_TYPE_PREFERENCE_OPTIONS, for display of stored shift-type values (Item 3 shiftTypesWorked). */
export const SHIFT_TYPE_LABELS: Record<EMTShiftTypePreference, string> = Object.fromEntries(
  SHIFT_TYPE_PREFERENCE_OPTIONS,
) as Record<EMTShiftTypePreference, string>;

/** EMT employment-type-preference UI options (Item 2). Values match JobTypeOption tokens. */
export const EMPLOYMENT_TYPE_PREFERENCE_OPTIONS: Array<[EMTEmploymentTypePreference, string]> = [
  ['full_time', 'Full-Time'],
  ['part_time', 'Part-Time'],
  ['per_diem', 'Per-Diem'],
];

/**
 * The employer's OWN requisition number for a job ("EMS-2026-114"), typed on the form, free text.
 * Stored as `employerReqId`. Not to be confused with `requisitionId`: Arevina's sequential number
 * (1001, 1002…), assigned by the server, immutable, never written by a client.
 */
export const EMPLOYER_REQ_ID_MAX_CHARS = 40;

export function normalizeEmployerReqId(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim().slice(0, EMPLOYER_REQ_ID_MAX_CHARS).trim();
}

/**
 * Optional job details (2026-10-09, the ApplicantStack Jobs list columns MedEx uses):
 *   department         free text, e.g. "Field Operations"
 *   flsaType           pay classification, 'hourly_non_exempt' | 'salaried_exempt' ("Job Type" in
 *                      ApplicantStack), alongside the employment types (full time, part time, per diem)
 *   campaignStartDate  the posting start date, 'YYYY-MM-DD'; when it is not set the posted date counts
 *                      (EmployerPostJob stamps today's date when a job first goes live without one)
 * All three are optional, never required to post, and absent on every job saved before them.
 */
export type FlsaTypeOption = 'hourly_non_exempt' | 'salaried_exempt';

export const FLSA_TYPE_OPTIONS: Array<[FlsaTypeOption, string]> = [
  ['hourly_non_exempt', 'Hourly, non-exempt'],
  ['salaried_exempt', 'Salaried, exempt'],
];

export function normalizeFlsaType(value: unknown): FlsaTypeOption | '' {
  return value === 'hourly_non_exempt' || value === 'salaried_exempt' ? value : '';
}

/** "Hourly, non-exempt", "Salaried, exempt", or '' when not set. */
export function flsaTypeLabel(value: unknown): string {
  const v = normalizeFlsaType(value);
  return v ? FLSA_TYPE_OPTIONS.find(([k]) => k === v)![1] : '';
}

export const DEPARTMENT_MAX_CHARS = 80;

export function normalizeDepartment(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim().slice(0, DEPARTMENT_MAX_CHARS).trim();
}

/** A real calendar date as 'YYYY-MM-DD' (what an <input type="date"> gives), else ''. */
export function normalizeCampaignStartDate(value: unknown): string {
  if (typeof value !== 'string') return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return '';
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return '';
  if (y < 2000 || y > 2100) return '';
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** The viewer's local calendar date as 'YYYY-MM-DD' (the default posting start date). */
export function localDateKey(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/**
 * Posting end date (2026-11-05, Phase 2A): optional 'YYYY-MM-DD'. Saved as `postingEndDate` plus
 * `expiresAt`, the END of that day in the employer's own time zone (a Date; Firestore stores it as a
 * Timestamp). `expiresAt` is what every reader already treats as "Expired" (jobOpenState.ts, the Jobs
 * list, the public board), and what the jobPostingExpiry scheduled function closes (and reminds about
 * 3 days before, src/lib/jobs/jobExpiry.ts). Same date rules as the posting start date.
 */
export const normalizePostingEndDate = normalizeCampaignStartDate;

/** 23:59:59.999 local time on the given 'YYYY-MM-DD', or null for a blank / invalid date. */
export function postingEndDateToExpiresAt(dateKey: unknown): Date | null {
  const key = normalizePostingEndDate(dateKey);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

/** A stored expiresAt (Timestamp, Date, ISO string, millis) as a local 'YYYY-MM-DD', else ''. */
function expiresAtToDateKey(value: unknown): string {
  let date: Date | null = null;
  if (value instanceof Date) date = value;
  else if (value && typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    date = (value as { toDate: () => Date }).toDate();
  } else if (typeof value === 'string' || typeof value === 'number') date = new Date(value);
  return date && !Number.isNaN(date.getTime()) ? localDateKey(date) : '';
}

export type JobFormValues = {
  title: string;
  /** The employer's own requisition number (optional, free text). See EMPLOYER_REQ_ID_MAX_CHARS. */
  employerReqId: string;
  /** Optional department, e.g. "Field Operations". See DEPARTMENT_MAX_CHARS. */
  department: string;
  /** Optional pay classification ('' = not set). See FLSA_TYPE_OPTIONS. */
  flsaType: FlsaTypeOption | '';
  /** Optional posting start date 'YYYY-MM-DD' ('' = the posted date). */
  campaignStartDate: string;
  /** Optional posting end date 'YYYY-MM-DD' ('' = stays open until closed). See postingEndDateToExpiresAt. */
  postingEndDate: string;
  /** "Physical requirements", one per entry (e.g. "Must be able to lift and carry 125 pounds"). */
  physicalRequirements: string[];
  /** "Minimum qualifications", one per entry. */
  minimumQualifications: string[];
  locationType: LocationTypeOption;
  locationText: string;
  locationCity?: string;
  locationStateCode?: string;
  locationStateName?: string;
  locationCounty?: string;
  locationLat?: number;
  locationLng?: number;
  /** Optional reference to organizations/{orgId}/locations/{locationId} (multi-location support). */
  locationId?: string | null;
  openToRelocation: boolean;
  /** '' = not set (optional). */
  hiringTimeline: HiringTimelineOption | '';
  hiresIn30Days: number;
  jobTypes: JobTypeOption[];
  schedule: ScheduleOption;
  summary: string;
  /** Key responsibilities — one bullet per entry. Legacy jobs stored a single free-text string. */
  keyResponsibilities: string[];
  /** Required qualifications beyond the certification / license / experience fields (one per entry). */
  requiredQualifications: string[];
  /** Nice-to-haves (one per entry). */
  preferredQualifications: string[];
  /** Shift pattern in the employer's words, e.g. "Four 12-hour shifts, 0600–1800, rotating weekends". */
  scheduleDetails: string;
  /** Pay notes the numbers cannot carry, e.g. differentials, sign-on bonus, overtime. */
  compensationDetails: string;
  /** "What We Offer" — the employer's own benefit bullets, shown with the preset benefits, apart from pay. */
  whatWeOffer: string[];
  /** "Additional Information" — catch-all prose (HIPAA / confidentiality notes, hours vary, paid or unpaid…). */
  additionalInformation: string;
  highlights: string;
  payVisibility: PayVisibilityOption;
  payUnit: PayUnitOption;
  minPay: number | undefined;
  maxPay: number | undefined;
  hoursMode: HoursModeOption;
  hoursPerWeek: number | undefined;
  benefits: string[];
  requiredCertifications: string[];
  preferredCertifications: string[];
  /**
   * "National Registry (NREMT) certification required". Off (default): in a state that certifies
   * without NREMT, a State EMS Certification for that state meets an NREMT-level requirement.
   */
  nremtRequired: boolean;
  /** "Must hold our state's license (no Compact privilege)". Off by default. */
  stateLicenseRequired: boolean;
  /**
   * "Accept applications from people missing a required credential". Off by default for every job,
   * existing and new: applying then needs every required credential Documents Reviewed (today's gate).
   * On: the candidate may apply anyway, flagged "Missing: X" and ranked below complete applicants
   * (applyToJob reads this from the job document).
   */
  acceptMissingCredentials: boolean;
  /**
   * Role category (flag driver_roles): 'driver' for a driver job, 'ems' when the employer picked EMS,
   * '' when never set (every job posted before 2026-10-09, read as EMS). '' is never written, so an
   * edit leaves an existing job's stored fields exactly as they were.
   */
  roleCategory: '' | 'ems' | 'driver' | 'dispatch';
  selectedRequirementPackId: string;
  preferredExperience: string;
  preferredEducation: string;
  /** Match scoring — minimum years (empty = no requirement). */
  requiredYears: string;
  /** Match scoring — required skills. */
  requiredSkills: string[];
  preferredExperienceTypes: string[];
  preferredSkills: string[];
  credentialingRequirements: JobCredentialingRequirements | undefined;
  onboardingSpeedPreference: OnboardingSpeedPreference;
  licenseRequired: string;
  tags: string;
  screeningQuestions: ScreeningQuestion[];
  /**
   * Standard application questions this job asks (work eligibility, 18+, start date…). Every one is
   * OFF unless the employer turns it on. Answers are stored and shown as submitted — never scored.
   */
  standardScreeningQuestions: StandardScreeningKey[];
  /** Video introduction request — toggle + custom prompt, set at job-posting time. */
  videoIntroEnabled: boolean;
  videoIntroPrompt: string;
  /**
   * "Consider candidates with a DUI in the last 5 years?" Default true (yes = nothing stored). false
   * is stored as drivingPolicy: { considerRecentDui: false }. Shown only while the
   * driving_record_attestation flag is on, but always round-tripped so an edit never drops it.
   * Never blocks applying; see src/lib/drivingRecord.ts.
   */
  considerRecentDui: boolean;
};

export const DEFAULT_JOB_FORM_VALUES: JobFormValues = {
  title: '',
  employerReqId: '',
  department: '',
  flsaType: '',
  campaignStartDate: '',
  postingEndDate: '',
  physicalRequirements: [],
  minimumQualifications: [],
  locationType: 'in_person',
  locationText: '',
  locationCity: '',
  locationStateCode: '',
  locationStateName: '',
  locationCounty: '',
  locationLat: undefined,
  locationLng: undefined,
  locationId: null,
  openToRelocation: false,
  hiringTimeline: '',
  hiresIn30Days: 1,
  jobTypes: [],
  schedule: '',
  summary: '',
  keyResponsibilities: [],
  requiredQualifications: [],
  preferredQualifications: [],
  scheduleDetails: '',
  compensationDetails: '',
  whatWeOffer: [],
  additionalInformation: '',
  highlights: '',
  payVisibility: 'range',
  payUnit: 'hour',
  minPay: undefined,
  maxPay: undefined,
  hoursMode: '',
  hoursPerWeek: undefined,
  benefits: [],
  requiredCertifications: [],
  preferredCertifications: [],
  nremtRequired: false,
  stateLicenseRequired: false,
  acceptMissingCredentials: false,
  roleCategory: '',
  selectedRequirementPackId: '',
  preferredExperience: '',
  preferredEducation: '',
  requiredYears: '',
  requiredSkills: [],
  preferredExperienceTypes: [],
  preferredSkills: [],
  credentialingRequirements: undefined,
  onboardingSpeedPreference: null,
  licenseRequired: '',
  tags: '',
  screeningQuestions: [],
  standardScreeningQuestions: [],
  videoIntroEnabled: false,
  videoIntroPrompt: '',
  considerRecentDui: true,
};

function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const o = value as { name?: string; title?: string; label?: string };
    return o.name || o.title || o.label || '';
  }
  return '';
}

function cleanCredentialingRequirements(
  cr?: JobCredentialingRequirements
): JobCredentialingRequirements | undefined {
  if (!cr) return undefined;
  const out: JobCredentialingRequirements = {};
  if (cr.requiredItems?.length) out.requiredItems = cr.requiredItems;
  if (cr.requiredCourses?.length) out.requiredCourses = cr.requiredCourses;
  const county = cr.countyName?.trim();
  if (county) out.countyName = county;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Hydrate form state from any Firestore job document (new or legacy). */
export function jobDocumentToFormValues(job: Record<string, unknown>): JobFormValues {
  const { required, preferred } = getJobCertRequirements(job as Parameters<typeof getJobCertRequirements>[0]);
  const locationType = (job.locationType as LocationTypeOption) || 'in_person';
  const locationText =
    typeof job.locationText === 'string'
      ? job.locationText
      : locationType === 'remote'
        ? ''
        : asString(job.location);

  const jobLoc = job.jobLocation as Record<string, unknown> | undefined;
  const locationCity = typeof jobLoc?.city === 'string' ? jobLoc.city : '';
  const locationStateCode =
    typeof jobLoc?.stateCode === 'string'
      ? jobLoc.stateCode
      : typeof job.locationState === 'string'
        ? job.locationState
        : '';
  const locationStateName = typeof jobLoc?.stateName === 'string' ? jobLoc.stateName : '';
  const locationCounty = typeof jobLoc?.county === 'string' ? jobLoc.county : '';
  const locationLat = typeof jobLoc?.lat === 'number' ? jobLoc.lat : undefined;
  const locationLng = typeof jobLoc?.lng === 'number' ? jobLoc.lng : undefined;

  let summary = typeof job.summary === 'string' ? job.summary : '';
  if (!summary.trim()) {
    const canonical = getCanonicalJobDescription(job);
    if (canonical) summary = canonical;
  }

  const jobTypes = Array.isArray(job.jobTypes)
    ? (job.jobTypes as JobTypeOption[]).filter(Boolean)
    : [];

  const requiredYearsRaw = job.requiredYears;
  const requiredYears =
    typeof requiredYearsRaw === 'number' && requiredYearsRaw >= 0 ? String(requiredYearsRaw) : '';

  return {
    ...DEFAULT_JOB_FORM_VALUES,
    title: asString(job.title),
    employerReqId: normalizeEmployerReqId(job.employerReqId),
    department: normalizeDepartment(job.department),
    flsaType: normalizeFlsaType(job.flsaType),
    campaignStartDate: normalizeCampaignStartDate(job.campaignStartDate),
    // The stored end date; a job that only has an expiresAt (set another way) shows that day.
    postingEndDate: normalizePostingEndDate(job.postingEndDate) || expiresAtToDateKey(job.expiresAt),
    physicalRequirements: normalizeJobList(job.physicalRequirements, 'physicalRequirements'),
    minimumQualifications: normalizeJobList(job.minimumQualifications, 'minimumQualifications'),
    locationType,
    locationText,
    locationCity,
    locationStateCode,
    locationStateName,
    locationCounty,
    locationLat,
    locationLng,
    locationId: typeof job.locationId === 'string' ? job.locationId : null,
    openToRelocation: job.openToRelocation === true,
    hiringTimeline: (job.hiringTimeline as HiringTimelineOption) || '',
    hiresIn30Days:
      typeof job.hiresIn30Days === 'number' && job.hiresIn30Days >= 1 ? job.hiresIn30Days : 1,
    jobTypes,
    schedule: (job.schedule as ScheduleOption) || '',
    summary,
    // Jobs posted before structured sections kept responsibilities as one free-text string;
    // each of its lines becomes a bullet so the employer edits (and candidates read) a real list.
    keyResponsibilities: normalizeJobList(
      Array.isArray(job.keyResponsibilities) ? job.keyResponsibilities : job.responsibilities,
      'keyResponsibilities'
    ),
    requiredQualifications: normalizeJobList(job.requiredQualifications, 'requiredQualifications'),
    preferredQualifications: normalizeJobList(job.preferredQualifications, 'preferredQualifications'),
    scheduleDetails: normalizeJobText(job.scheduleDetails, 'scheduleDetails'),
    compensationDetails: normalizeJobText(job.compensationDetails, 'compensationDetails'),
    whatWeOffer: normalizeJobList(job.whatWeOffer, 'whatWeOffer'),
    additionalInformation: normalizeJobText(job.additionalInformation, 'additionalInformation'),
    highlights: typeof job.highlights === 'string' ? job.highlights : '',
    payVisibility: (job.payVisibility as PayVisibilityOption) || 'range',
    payUnit: (job.payUnit as PayUnitOption) || 'hour',
    minPay: typeof job.minPay === 'number' ? job.minPay : undefined,
    maxPay: typeof job.maxPay === 'number' ? job.maxPay : undefined,
    hoursMode: (job.hoursMode as HoursModeOption) || '',
    hoursPerWeek: typeof job.hoursPerWeek === 'number' ? job.hoursPerWeek : undefined,
    benefits: Array.isArray(job.benefits) ? (job.benefits as string[]) : [],
    requiredCertifications: required,
    preferredCertifications: preferred,
    nremtRequired:
      (job.certificationRequirements as { nremtRequired?: unknown } | null | undefined)?.nremtRequired === true,
    stateLicenseRequired:
      (job.certificationRequirements as { stateLicenseRequired?: unknown } | null | undefined)?.stateLicenseRequired === true,
    acceptMissingCredentials: job.acceptMissingCredentials === true,
    roleCategory: job.roleCategory === 'driver' || job.roleCategory === 'ems' || job.roleCategory === 'dispatch' ? job.roleCategory : '',
    selectedRequirementPackId: typeof job.requirementPackId === 'string' ? job.requirementPackId : '',
    preferredExperience: typeof job.preferredExperience === 'string' ? job.preferredExperience : '',
    preferredEducation: typeof job.preferredEducation === 'string' ? job.preferredEducation : '',
    requiredYears,
    requiredSkills: Array.isArray(job.requiredSkills) ? (job.requiredSkills as string[]) : [],
    preferredExperienceTypes: Array.isArray(job.preferredExperienceTypes)
      ? (job.preferredExperienceTypes as string[])
      : [],
    preferredSkills: Array.isArray(job.preferredSkills) ? (job.preferredSkills as string[]) : [],
    credentialingRequirements: cleanCredentialingRequirements(
      job.credentialingRequirements as JobCredentialingRequirements | undefined
    ),
    onboardingSpeedPreference:
      (job.onboardingSpeedPreference as OnboardingSpeedPreference) ?? null,
    licenseRequired: typeof job.licenseRequired === 'string' ? job.licenseRequired : '',
    tags: Array.isArray(job.tags) ? (job.tags as string[]).join(', ') : '',
    // Lenient: a question the employer saved is shown to them even if it needs fixing (validateJobForm says what).
    screeningQuestions: normalizeCustomQuestions(job.screeningQuestions, 'lenient').map((q) => ({ ...q, required: q.required === true })),
    standardScreeningQuestions: normalizeStandardScreeningKeys(job.standardScreeningQuestions),
    videoIntroEnabled: (job.videoIntroRequest as VideoIntroRequest | undefined)?.enabled === true,
    videoIntroPrompt:
      typeof (job.videoIntroRequest as VideoIntroRequest | undefined)?.prompt === 'string'
        ? (job.videoIntroRequest as VideoIntroRequest).prompt
        : '',
    // Same rule as jobConsidersRecentDui (src/lib/drivingRecord.ts), inlined: this file is copied into
    // functions/src/generated/ and must not pull in more imports. Only an explicit false says no.
    considerRecentDui: (job.drivingPolicy as { considerRecentDui?: unknown } | null | undefined)?.considerRecentDui !== false,
  };
}

export type JobFormValidationResult = { ok: true } | { ok: false; message: string };

/** Shared validation for post, edit, and draft publish. */
export function validateJobForm(values: JobFormValues, isDraft: boolean, now: Date = new Date()): JobFormValidationResult {
  if (!values.title.trim()) {
    return { ok: false, message: 'Job title is required' };
  }
  // "* Job location type" and "* Hiring timeline" are selects with defaults, so they cannot be
  // left blank through the UI — checked anyway, so the asterisk never depends on that accident.
  if (!values.locationType) {
    return { ok: false, message: 'Job location type is required' };
  }
  if (values.locationType !== 'remote' && !values.locationText.trim()) {
    return { ok: false, message: 'Location is required for in-person or hybrid jobs' };
  }
  // Optional, but a typed date must be a real one (a draft too: it is saved as typed).
  if (values.campaignStartDate.trim() && !normalizeCampaignStartDate(values.campaignStartDate)) {
    return { ok: false, message: 'Posting start date must be a real date, or leave it blank to use the day you post' };
  }
  const endKey = normalizePostingEndDate(values.postingEndDate);
  if (values.postingEndDate.trim() && !endKey) {
    return { ok: false, message: 'Posting end date must be a real date, or leave it blank to keep the job open until you close it' };
  }
  const startKey = normalizeCampaignStartDate(values.campaignStartDate);
  if (endKey && startKey && endKey < startKey) {
    return { ok: false, message: 'Posting end date must be on or after the posting start date' };
  }
  // A live job with an end date already passed would close again within the hour.
  if (!isDraft && endKey && endKey < localDateKey(now)) {
    return { ok: false, message: 'Posting end date has passed. Pick today or a later date, or clear it' };
  }
  // Typed numbers can be negative or absurd (type="number" allows both).
  if ((values.minPay != null && values.minPay < 0) || (values.maxPay != null && values.maxPay < 0)) {
    return { ok: false, message: 'Pay cannot be negative' };
  }
  if (values.hoursMode === 'fixed' && values.hoursPerWeek != null && (values.hoursPerWeek < 1 || values.hoursPerWeek > 168)) {
    return { ok: false, message: 'Hours per week must be between 1 and 168' };
  }
  // The hiring timeline is optional (2026-10-09): many EMS employers hire year round.
  if (!isDraft && !(values.hiresIn30Days >= 1)) {
    return { ok: false, message: 'Number of hires must be at least 1' };
  }
  if (!isDraft && values.jobTypes.length === 0) {
    return { ok: false, message: 'At least one job type is required' };
  }
  // The employer's own questions: 1-300 character prompts, at most 10, 2-10 choices on a multiple-choice one.
  // A draft may hold a half-written question; posting or updating a live job may not.
  const questionError = isDraft ? undefined : customQuestionDefinitionErrors(values.screeningQuestions)[0];
  if (questionError) {
    return { ok: false, message: questionError.message };
  }
  if (values.videoIntroEnabled && !values.videoIntroPrompt.trim()) {
    return { ok: false, message: 'Add a prompt/question for the video introduction, or turn the toggle off' };
  }
  if (!isDraft && (!values.summary.trim() || values.summary.trim().length < 80)) {
    return { ok: false, message: 'Job summary must be at least 80 characters' };
  }
  // The label promises "80-800 characters"; only the lower bound was ever checked.
  if (values.summary.trim().length > JOB_SUMMARY_MAX_CHARS) {
    return { ok: false, message: `Job summary must be ${JOB_SUMMARY_MAX_CHARS} characters or fewer` };
  }
  if (!isDraft) {
    // A range needs a minimum; the maximum is optional ("Starting at $X/hr" when it is left blank).
    if (values.payVisibility === 'range' && !values.minPay) {
      return { ok: false, message: 'Starting pay is required' };
    }
    if (values.payVisibility === 'range' && values.maxPay && (values.minPay ?? 0) > values.maxPay) {
      return { ok: false, message: 'Invalid pay range: min must be less than or equal to max' };
    }
    if (values.payVisibility === 'starting_at' && !values.minPay) {
      return { ok: false, message: 'Starting pay is required' };
    }
    if (values.payVisibility === 'up_to' && !values.maxPay) {
      return { ok: false, message: 'Maximum pay is required' };
    }
    if (values.payVisibility === 'exact' && !values.minPay) {
      return { ok: false, message: 'Exact pay is required' };
    }
  }
  return { ok: true };
}

/** The pay numbers a form will save (also used for the live "Candidates will see" preview). */
export function resolvePayFields(values: JobFormValues): { minPay?: number; maxPay?: number } {
  if (values.payVisibility === 'range') {
    return { minPay: values.minPay, maxPay: values.maxPay || undefined };
  }
  if (values.payVisibility === 'starting_at') {
    return { minPay: values.minPay, maxPay: undefined };
  }
  if (values.payVisibility === 'up_to') {
    return { minPay: undefined, maxPay: values.maxPay };
  }
  if (values.payVisibility === 'exact') {
    return { minPay: values.minPay, maxPay: values.minPay };
  }
  return { minPay: values.minPay, maxPay: values.maxPay };
}

/** Build Firestore-ready job payload from form values. */
export function formValuesToJobPayload(
  values: JobFormValues,
  options: { isDraft: boolean; isUpdate?: boolean }
): Record<string, unknown> {
  const { minPay, maxPay } = resolvePayFields(values);
  // Saving strips `undefined`, which on an UPDATE leaves the old value in Firestore — an employer
  // who deleted every responsibility would still see them on the posting. A cleared section is
  // written as null on update (readers treat null as empty); on create there is nothing to clear.
  const cleared = options.isUpdate ? null : undefined;
  const keyResponsibilities = normalizeJobList(values.keyResponsibilities, 'keyResponsibilities');
  const requiredQualifications = normalizeJobList(values.requiredQualifications, 'requiredQualifications');
  const preferredQualifications = normalizeJobList(values.preferredQualifications, 'preferredQualifications');
  const scheduleDetails = normalizeJobText(values.scheduleDetails, 'scheduleDetails');
  const compensationDetails = normalizeJobText(values.compensationDetails, 'compensationDetails');
  const whatWeOffer = normalizeJobList(values.whatWeOffer, 'whatWeOffer');
  const additionalInformation = normalizeJobText(values.additionalInformation, 'additionalInformation');
  const physicalRequirements = normalizeJobList(values.physicalRequirements, 'physicalRequirements');
  const minimumQualifications = normalizeJobList(values.minimumQualifications, 'minimumQualifications');
  const tagsArray = values.tags
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const credentialing = cleanCredentialingRequirements(values.credentialingRequirements);
  const pack = values.selectedRequirementPackId
    ? getRequirementPackById(values.selectedRequirementPackId)
    : undefined;

  const requiredYearsNum = values.requiredYears.trim()
    ? Math.max(0, parseInt(values.requiredYears, 10) || 0)
    : undefined;

  const payload: Record<string, unknown> = {
    title: values.title.trim(),
    // The employer's own Req ID. Never `requisitionId` (server-assigned; rules reject a client write).
    employerReqId: normalizeEmployerReqId(values.employerReqId) || cleared,
    // Optional job details: a value cleared on an edit is removed (null), like the sections below.
    department: normalizeDepartment(values.department) || cleared,
    flsaType: normalizeFlsaType(values.flsaType) || cleared,
    campaignStartDate: normalizeCampaignStartDate(values.campaignStartDate) || cleared,
    // Posting end date: cleared on an edit means "open until closed" again, so expiresAt goes too.
    postingEndDate: normalizePostingEndDate(values.postingEndDate) || cleared,
    expiresAt: postingEndDateToExpiresAt(values.postingEndDate) ?? cleared,
    physicalRequirements: physicalRequirements.length > 0 ? physicalRequirements : cleared,
    minimumQualifications: minimumQualifications.length > 0 ? minimumQualifications : cleared,
    location: values.locationType !== 'remote' ? values.locationText.trim() : 'Remote',
    locationType: values.locationType,
    locationText: values.locationType !== 'remote' ? values.locationText.trim() : undefined,
    locationId: values.locationType !== 'remote' ? values.locationId || undefined : undefined,
    ...(values.locationType !== 'remote' &&
    (values.locationCity || values.locationStateCode || values.locationLat != null)
      ? {
          locationState: values.locationStateCode || undefined,
          jobLocation: {
            city: values.locationCity?.trim() || undefined,
            stateCode: values.locationStateCode?.trim().toUpperCase() || undefined,
            stateName: values.locationStateName?.trim() || undefined,
            county: values.locationCounty?.trim() || undefined,
            formattedAddress: values.locationText.trim() || undefined,
            lat: values.locationLat,
            lng: values.locationLng,
          },
          ...(values.locationLat != null && values.locationLng != null
            ? { geo: { lat: values.locationLat, lng: values.locationLng, precision: 'city' } }
            : {}),
        }
      : {}),
    openToRelocation: values.openToRelocation || undefined,
    hiringTimeline: values.hiringTimeline || null,
    hiresIn30Days: values.hiresIn30Days,
    jobTypes: values.jobTypes.length > 0 ? values.jobTypes : undefined,
    schedule: values.schedule || undefined,
    summary: values.summary.trim() || undefined,
    keyResponsibilities: keyResponsibilities.length > 0 ? keyResponsibilities : cleared,
    // Legacy single-string field, kept in step with the list so older readers never show stale text.
    responsibilities: keyResponsibilities.length > 0 ? keyResponsibilities.join('\n') : cleared,
    requiredQualifications: requiredQualifications.length > 0 ? requiredQualifications : cleared,
    preferredQualifications: preferredQualifications.length > 0 ? preferredQualifications : cleared,
    scheduleDetails: scheduleDetails || cleared,
    compensationDetails: compensationDetails || cleared,
    whatWeOffer: whatWeOffer.length > 0 ? whatWeOffer : cleared,
    additionalInformation: additionalInformation || cleared,
    highlights: values.highlights.trim() || cleared,
    description: values.summary.trim() || undefined,
    payVisibility: values.payVisibility,
    payUnit: values.payUnit,
    // Pay, hours, requirements and the video prompt follow the same rule: a value the employer cleared
    // on an edit must be removed, not silently kept (it kept gating eligibility and showing old pay).
    minPay: minPay ?? cleared,
    maxPay: maxPay ?? cleared,
    hoursMode: values.hoursMode || cleared,
    hoursPerWeek:
      values.hoursMode === 'fixed' && values.hoursPerWeek != null && Number.isFinite(values.hoursPerWeek)
        ? values.hoursPerWeek
        : cleared,
    // Same stale-on-edit trap as the sections above: un-ticking the last benefit must actually remove it.
    benefits: values.benefits.length > 0 ? values.benefits : cleared,
    licenseRequired: values.licenseRequired.trim() || cleared,
    tags: tagsArray.length > 0 ? tagsArray : cleared,
    // Always written as a boolean so an edit that switches it off really turns it off.
    acceptMissingCredentials: values.acceptMissingCredentials === true,
    // Role category (flag driver_roles): written only when the employer chose one. A job that never
    // had one keeps none (it reads as 'ems'), so saving an older job changes nothing about it.
    ...(values.roleCategory === 'driver' || values.roleCategory === 'ems' || values.roleCategory === 'dispatch' ? { roleCategory: values.roleCategory } : {}),
    ...(values.requiredCertifications.length > 0 || values.preferredCertifications.length > 0 || values.nremtRequired || values.stateLicenseRequired
      ? {
          certificationRequirements: {
            requiredCertifications:
              values.requiredCertifications.length > 0 ? values.requiredCertifications : undefined,
            preferredCertifications:
              values.preferredCertifications.length > 0 ? values.preferredCertifications : undefined,
            // Only written when ticked; null on an edit that unticks it (absent and null both read as false).
            nremtRequired: values.nremtRequired ? true : cleared,
            stateLicenseRequired: values.stateLicenseRequired ? true : cleared,
          },
        }
      : { certificationRequirements: cleared }),
    ...(pack
      ? {
          requirementPackId: pack.id,
          requirementPackLabel: pack.label,
          ...((packCredentialingItems(pack).length ||
            pack.requiredCourses?.length ||
            pack.countyName) &&
          !credentialing
            ? {
                credentialingRequirements: cleanCredentialingRequirements({
                  // What to bring, then after-offer (medical) and employer-arranged items (B1/B2): every
                  // reader splits them with splitCredentialingItems.
                  requiredItems: packCredentialingItems(pack).length ? packCredentialingItems(pack) : undefined,
                  requiredCourses: pack.requiredCourses?.length ? pack.requiredCourses : undefined,
                  countyName: pack.countyName ?? undefined,
                }),
              }
            : {}),
        }
      : { requirementPackId: cleared, requirementPackLabel: cleared }),
    ...(credentialing
      ? { credentialingRequirements: credentialing }
      : pack && (packCredentialingItems(pack).length || pack.requiredCourses?.length || pack.countyName)
        ? {}
        : { credentialingRequirements: cleared }),
    preferredExperience: values.preferredExperience.trim() || cleared,
    preferredEducation: values.preferredEducation.trim() || cleared,
    preferredExperienceTypes:
      values.preferredExperienceTypes.length > 0 ? values.preferredExperienceTypes : cleared,
    preferredSkills: values.preferredSkills.length > 0 ? values.preferredSkills : cleared,
    requiredSkills: values.requiredSkills.length > 0 ? values.requiredSkills : cleared,
    requiredYears: requiredYearsNum ?? cleared,
    onboardingSpeedPreference: values.onboardingSpeedPreference || cleared,
    // A plain "Update Job" must not reopen a CLOSED job (isOpen is what job boards and applyToJob
    // read). Going live (a new post, or a draft being posted) sets isOpen in EmployerPostJob.
    ...(options.isUpdate && !options.isDraft ? {} : { isOpen: !options.isDraft }),
    // A draft is its own lifecycle state. Without this, createJob defaulted status to "active"
    // while isOpen was false, and Manage Jobs read that pair as a CLOSED job with a "Posted" date.
    // Going live is not set here: the caller (EmployerPostJob) writes status "active" + postedAt
    // when a job is first posted, and leaves status alone on a plain "Update Job".
    ...(options.isDraft ? { status: 'draft' as const } : {}),
    // Same stale-on-edit trap as the posting sections: removing the last custom question on an edit
    // used to leave it on the job (undefined is stripped), so applicants were still asked it.
    screeningQuestions:
      values.screeningQuestions.length > 0
        ? cleanCustomQuestionsForSave(values.screeningQuestions).map((q) => ({ ...q, required: q.required === true }))
        : cleared,
    // `cleared` matters here: turning the last question OFF on an edit must actually stop it being asked.
    standardScreeningQuestions:
      values.standardScreeningQuestions.length > 0
        ? normalizeStandardScreeningKeys(values.standardScreeningQuestions)
        : cleared,
    videoIntroRequest:
      values.videoIntroEnabled && values.videoIntroPrompt.trim()
        ? ({ enabled: true, prompt: values.videoIntroPrompt.trim() } satisfies VideoIntroRequest)
        : cleared,
    // Default yes is stored as nothing; switching back to yes on an edit removes the setting.
    drivingPolicy: values.considerRecentDui === false ? { considerRecentDui: false } : cleared,
  };

  return payload;
}

/** Normalize job fields for match scoring / display (backward compatible defaults). */
export function normalizeJobMatchFields(job: Record<string, unknown>): {
  requiredYears: number;
  requiredSkills: string[];
  openToRelocation: boolean;
} {
  const y = job.requiredYears;
  const requiredYears = typeof y === 'number' && y >= 0 ? y : 0;
  const requiredSkills = Array.isArray(job.requiredSkills) ? (job.requiredSkills as string[]) : [];
  return {
    requiredYears,
    requiredSkills,
    openToRelocation: job.openToRelocation === true,
  };
}

export function formatJobTypes(jobTypes: unknown): string {
  if (!Array.isArray(jobTypes) || jobTypes.length === 0) return '';
  return jobTypes
    .map((t) => {
      if (t === 'emergency_deployment') return 'Emergency Deployment';
      return String(t)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    })
    .join(', ');
}

/** Compact employer list-card summary of match-scoring fields. */
export function formatJobMatchSummary(job: Record<string, unknown>): string {
  const { requiredYears, requiredSkills, openToRelocation } = normalizeJobMatchFields(job);
  const parts: string[] = [];
  const jobTypesLabel = formatJobTypes(job.jobTypes);
  if (jobTypesLabel) parts.push(jobTypesLabel);
  if (requiredYears > 0) parts.push(`${requiredYears}+ yrs experience`);
  if (requiredSkills.length > 0) {
    const shown = requiredSkills.slice(0, 3).join(', ');
    parts.push(
      requiredSkills.length > 3 ? `Skills: ${shown} +${requiredSkills.length - 3} more` : `Skills: ${shown}`
    );
  }
  if (openToRelocation) parts.push('Open to relocation');
  return parts.join(' · ');
}
